export interface ApiError {
  message: string;
  code?: string;
  statusCode?: number;
  details?: unknown;
}

export interface FetchRequestConfig {
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
  [key: string]: unknown;
}

interface RequestInterceptor {
  onFulfilled?: (config: FetchRequestConfig & { url: string; init: RequestInit }) => FetchRequestConfig & { url: string; init: RequestInit } | Promise<FetchRequestConfig & { url: string; init: RequestInit }>;
  onRejected?: (error: unknown) => unknown;
}

interface ResponseInterceptor {
  onFulfilled?: (response: Response) => Response | Promise<Response>;
  onRejected?: (error: unknown) => unknown;
}

class FetchClient {
  private baseURL: string;
  private defaultTimeout: number;
  private defaultHeaders: Record<string, string>;
  private authToken: string | null = null;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];

  constructor(config: {
    baseURL?: string;
    timeout?: number;
    headers?: Record<string, string>;
  } = {}) {
    this.baseURL = config.baseURL || '';
    this.defaultTimeout = config.timeout || 30000;
    this.defaultHeaders = config.headers || {};
  }

  // Interceptor management
  interceptors = {
    request: {
      use: (onFulfilled?: RequestInterceptor['onFulfilled'], onRejected?: RequestInterceptor['onRejected']) => {
        this.requestInterceptors.push({ onFulfilled, onRejected });
        return this.requestInterceptors.length - 1;
      },
    },
    response: {
      use: (onFulfilled?: ResponseInterceptor['onFulfilled'], onRejected?: ResponseInterceptor['onRejected']) => {
        this.responseInterceptors.push({ onFulfilled, onRejected });
        return this.responseInterceptors.length - 1;
      },
    },
  };

  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private async applyRequestInterceptors(config: FetchRequestConfig & { url: string; init: RequestInit }): Promise<FetchRequestConfig & { url: string; init: RequestInit }> {
    let result = config;

    for (const interceptor of this.requestInterceptors) {
      if (interceptor.onFulfilled) {
        try {
          result = await interceptor.onFulfilled(result);
        } catch (error) {
          if (interceptor.onRejected) {
            await interceptor.onRejected(error);
          }
          throw error;
        }
      }
    }

    return result;
  }

  private async applyResponseInterceptors(response: Response): Promise<Response> {
    let result = response;

    for (const interceptor of this.responseInterceptors) {
      if (interceptor.onFulfilled && response.ok) {
        try {
          result = await interceptor.onFulfilled(result);
        } catch (error) {
          if (interceptor.onRejected) {
            await interceptor.onRejected(error);
          }
          throw error;
        }
      } else if (interceptor.onRejected && !response.ok) {
        await interceptor.onRejected(this.createErrorFromResponse(result));
      }
    }

    return result;
  }

  private async createErrorFromResponse(response: Response): Promise<ApiError> {
    let errorData: Record<string, unknown> = {};

    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else {
        errorData = { message: await response.text() };
      }
    } catch {
      // If we can't parse the error body, use status text
      errorData = { message: response.statusText };
    }

    return {
      message: (errorData.message as string) || `HTTP ${response.status}: ${response.statusText}`,
      code: errorData.code as string | undefined,
      statusCode: response.status,
      details: errorData.details,
    };
  }

  private createTimeoutSignal(timeout?: number): AbortSignal {
    const controller = new AbortController();
    const timeoutMs = timeout ?? this.defaultTimeout;

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    // Clear timeout if request completes normally
    const originalSignal = controller.signal;
    const clearTimeoutOnComplete = () => {
      clearTimeout(timeoutId);
    };

    originalSignal.addEventListener('abort', clearTimeoutOnComplete);

    return controller.signal;
  }

  private async request<T = unknown>(
    method: string,
    url: string,
    data?: unknown,
    config: FetchRequestConfig = {}
  ): Promise<T> {
    // Construct full URL
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;

    // Prepare headers
    const headers: Record<string, string> = {
      ...this.defaultHeaders,
      ...config.headers,
    };

    // Add auth token if available
    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    // Prepare request init
    const init: RequestInit = {
      method,
      headers,
      signal: config.signal || this.createTimeoutSignal(config.timeout),
    };

    // Add body for methods that support it
    if (data !== undefined && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
      if (data instanceof FormData) {
        init.body = data;
        // Remove Content-Type header for FormData (browser will set it automatically with boundary)
        delete headers['Content-Type'];
      } else if (typeof data === 'string') {
        init.body = data;
      } else {
        init.body = JSON.stringify(data);
        headers['Content-Type'] = 'application/json';
      }
    }

    // Apply request interceptors
    let requestConfig = { url: fullUrl, init, ...config };
    requestConfig = await this.applyRequestInterceptors(requestConfig);

    // Make the request
    let response: Response;
    try {
      response = await fetch(requestConfig.url, requestConfig.init);
    } catch (error) {
      // Handle network errors
      const networkError = error as { name?: string };
      if (networkError.name === 'AbortError') {
        throw {
          message: 'Request timeout',
          code: 'TIMEOUT_ERROR',
        } as ApiError;
      }

      throw {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      } as ApiError;
    }

    // Apply response interceptors
    response = await this.applyResponseInterceptors(response);

    // Handle non-OK responses
    if (!response.ok) {
      throw await this.createErrorFromResponse(response);
    }

    // Parse response
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else if (method.toUpperCase() === 'DELETE') {
      // DELETE requests might not return content
      return undefined as T;
    } else {
      return await response.text() as T;
    }
  }

  async get<T = unknown>(url: string, config?: FetchRequestConfig): Promise<T> {
    return this.request<T>('GET', url, undefined, config);
  }

  async post<T = unknown>(url: string, data?: unknown, config?: FetchRequestConfig): Promise<T> {
    return this.request<T>('POST', url, data, config);
  }

  async put<T = unknown>(url: string, data?: unknown, config?: FetchRequestConfig): Promise<T> {
    return this.request<T>('PUT', url, data, config);
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: FetchRequestConfig): Promise<T> {
    return this.request<T>('PATCH', url, data, config);
  }

  async delete<T = unknown>(url: string, config?: FetchRequestConfig): Promise<T> {
    return this.request<T>('DELETE', url, undefined, config);
  }

  async upload<T = unknown>(
    url: string,
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<T> {
    // Note: fetch doesn't have built-in upload progress support
    // For upload progress, you would typically need to use XMLHttpRequest or a library
    // For now, we'll just make the request without progress tracking
    if (onProgress) {
      console.warn('Upload progress is not supported with native fetch. Consider using XMLHttpRequest for progress tracking.');
    }

    return this.request<T>('POST', url, formData, {
      headers: {
        // Don't set Content-Type for FormData - browser handles it
      },
    });
  }
}

// Factory function to create client instance
export function createFetchClient(config: {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
} = {}): FetchClient {
  return new FetchClient(config);
}

export { FetchClient };